import { create } from "zustand";

export type RealtimeConnectionStatus = "idle" | "connecting" | "connected" | "disconnected";

export interface RealtimeTranscriptEntry {
  id: string;
  role: "user" | "assistant";
  text: string;
  createdAt: number;
}

interface RealtimeChatState {
  status: RealtimeConnectionStatus;
  messages: RealtimeTranscriptEntry[];
  connectionId?: string;
  error?: string;
  inputLevel: number;
  setStatus: (status: RealtimeConnectionStatus) => void;
  setConnectionId: (id: string | undefined) => void;
  addMessage: (entry: RealtimeTranscriptEntry) => void;
  updateMessage: (id: string, update: Partial<RealtimeTranscriptEntry>) => void;
  setError: (message?: string) => void;
  setInputLevel: (level: number) => void;
  reset: () => void;
}

function generateId() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

export const useRealtimeChatStore = create<RealtimeChatState>(set => ({
  status: "idle",
  messages: [],
  inputLevel: 0,
  setStatus: status => set(state => (state.status === status ? state : { status })),
  setConnectionId: connectionId => set({ connectionId }),
  addMessage: entry => set(state => ({ messages: [...state.messages, entry] })),
  updateMessage: (id, update) =>
    set(state => ({
      messages: state.messages.map(message =>
        message.id === id ? { ...message, ...update } : message,
      ),
    })),
  setError: error => set(state => (state.error === error ? state : { error })),
  setInputLevel: inputLevel => set({ inputLevel }),
  reset: () =>
    set({
      status: "idle",
      messages: [],
      connectionId: undefined,
      error: undefined,
      inputLevel: 0,
    }),
}));

export function createUserTranscriptEntry(text: string): RealtimeTranscriptEntry {
  return {
    id: generateId(),
    role: "user",
    text,
    createdAt: Date.now(),
  };
}

export function createAssistantTranscriptEntry(): RealtimeTranscriptEntry {
  return {
    id: generateId(),
    role: "assistant",
    text: "",
    createdAt: Date.now(),
  };
}

export function appendAssistantText(
  entry: RealtimeTranscriptEntry,
  delta: string,
): RealtimeTranscriptEntry {
  return {
    ...entry,
    text: entry.text + delta,
  };
}
